import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { AcceptanceMethod } from "client/api-v2";
import type { OrderDto } from "client/api-v2";
import { acceptOrderOnBehalf } from "areas/orders/services/orderService";
import { describeOrderError } from "areas/orders/utils/orderErrors";

interface AcceptOnBehalfFormValues {
  acceptedByName: string;
  acceptedByCompany: string;
  method: AcceptanceMethod;
}

const defaultValues: AcceptOnBehalfFormValues = {
  acceptedByName: "",
  acceptedByCompany: "",
  method: AcceptanceMethod.ClickThrough,
};

const formId = "accept-on-behalf-form";
const acceptedByMaxLength = 200;

export function AcceptOnBehalfDialog({
  order,
  open,
  onClose,
  onAccepted,
}: {
  order: OrderDto;
  open: boolean;
  onClose: () => void;
  onAccepted: (order: OrderDto) => void;
}) {
  const [serverError, setServerError] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<AcceptOnBehalfFormValues>({ defaultValues });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setServerError(undefined);
    }
  }, [open, reset]);

  const cancel = () => !isSubmitting && onClose();

  const onSubmit = async (values: AcceptOnBehalfFormValues) => {
    setServerError(undefined);
    try {
      onAccepted(
        await acceptOrderOnBehalf(order.odNumber, {
          acceptedByName: values.acceptedByName.trim(),
          acceptedByCompany: values.acceptedByCompany.trim(),
          method: values.method,
          version: order.version,
        })
      );
    } catch (error) {
      setServerError(describeOrderError(error));
    }
  };

  return (
    <Dialog open={open} onClose={cancel} maxWidth="sm" fullWidth>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", pr: 1 }}>
        <DialogTitle sx={{ flex: 1 }}>Accept on the customer&rsquo;s behalf</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={cancel}
          disabled={isSubmitting}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <DialogContent>
        <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            {serverError && (
              <Grid size={12}>
                <Alert severity="error">{serverError}</Alert>
              </Grid>
            )}
            <Grid size={12}>
              <Controller
                name="method"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <FormLabel id="accept-on-behalf-method-label">How the customer accepted</FormLabel>
                    <RadioGroup {...field} aria-labelledby="accept-on-behalf-method-label">
                      <FormControlLabel
                        value={AcceptanceMethod.ClickThrough}
                        control={<Radio />}
                        label="Accepted the purchase in writing through other means, e.g."
                      />
                      <Box component="ul" sx={{ m: 0, mb: 1, ml: 4, listStyle: "disc inside" }}>
                        {["Customer has provided a purchase order", "Email (that's fine, send me an invoice)"].map(
                          (example) => (
                            <Typography component="li" variant="body2" color="text.secondary" key={example}>
                              {example}
                            </Typography>
                          )
                        )}
                      </Box>
                      <FormControlLabel
                        value={AcceptanceMethod.SignedDocument}
                        control={<Radio />}
                        label="They have signed the quote document"
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name="acceptedByName"
                control={control}
                rules={{ validate: (value) => value.trim() !== "" || "The customer's name is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    label="Accepted by"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    slotProps={{ htmlInput: { maxLength: acceptedByMaxLength } }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name="acceptedByCompany"
                control={control}
                rules={{ validate: (value) => value.trim() !== "" || "The customer's company is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    label="Company"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    slotProps={{ htmlInput: { maxLength: acceptedByMaxLength } }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary">
                Accepting is final. The acceptance records you as the person who took it, from this browser and address.
              </Typography>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" form={formId} variant="contained" loading={isSubmitting}>
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
}
